import React, { useRef, useState } from 'react';
import {
    Card, Typography, Row, Col, Statistic, Divider, Tag, Table, Spin, Tooltip as AntTooltip,
    Space, Modal, Button, List, message, Select
} from 'antd';
import {
    FallOutlined, RiseOutlined, DollarOutlined, InfoCircleOutlined, EyeOutlined,
    ClockCircleOutlined, UserOutlined, PrinterOutlined, FileTextOutlined, FilePdfOutlined
} from '@ant-design/icons';
import { useGetInformesCaja, useGetDetalleCaja, useGetProductosDeVenta } from '../../../hooks/useInformes';
import { TicketCierreCaja } from '../../cierre_caja/TicketCierreCaja';
import { useReactToPrint } from 'react-to-print';
import { useConfig } from '../../../context/ConfigContext';
import { TicketVenta } from '../../ventas/TicketVenta';
import { pdf } from '@react-pdf/renderer';
import { FacturaFormal } from '../../ventas/FacturaFormal';
import { generarTicketBluetooth } from '../../../utils/impresionBluetooth';
import { useAuth } from '../../../aws/AuthContext';
import axios from 'axios';
import dayjs from 'dayjs';
const API_BASE_URL = process.env.REACT_APP_API_URL;

const { Title, Text } = Typography;

const SeccionFlujoCaja = ({ desde, hasta }) => {

    const { config } = useConfig();

    const { getJwtToken } = useAuth();
    const [notasSesion, setNotasSesion] = useState([]);

    const [sesionIdSeleccionada, setSesionIdSeleccionada] = useState(null);
    const [ventaSeleccionada, setVentaSeleccionada] = useState(null);
    const [datosImpresionCierre, setDatosImpresionCierre] = useState(null);
    const [datosImpresionVenta, setDatosImpresionVenta] = useState(null);
    const [usuarioFiltro, setUsuarioFiltro] = useState('todos');
    const ventaPrintComponentRef = useRef(null);
    const handlePrintVenta = useReactToPrint({
        contentRef: ventaPrintComponentRef,
        documentTitle: "Ticket de Venta",
    });

    const { data, isLoading, isError } = useGetInformesCaja({ desde, hasta });

    const { data: detalle, isLoading: loadingDetalle } = useGetDetalleCaja(sesionIdSeleccionada);
    const { data: productosVenta, isLoading: loadingVenta } = useGetProductosDeVenta(ventaSeleccionada?.id);

    React.useEffect(() => {
        const fetchNotasDelTurno = async () => {
            if (!sesionIdSeleccionada || !detalle?.info) return;
            try {
                const token = await getJwtToken();
                const res = await axios.get(`${API_BASE_URL}/v1-notas-credito`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                const formatoStr = "DD/MM/YYYY hh:mm A";
                const apertura = dayjs(detalle.info.apertura, formatoStr);
                const cierre = detalle.info.cierre ? dayjs(detalle.info.cierre, formatoStr) : dayjs();

                const notasTurno = (res.data || []).filter(nota => {
                    const fechaNota = dayjs(nota.created_at);
                    return fechaNota.isAfter(apertura) && fechaNota.isBefore(cierre.add(1, 'minute'));
                });
                setNotasSesion(notasTurno);
            } catch (error) {
                console.error("Error cargando notas de crédito del turno:", error);
            }
        };
        fetchNotasDelTurno();
    }, [sesionIdSeleccionada, detalle, getJwtToken]);

    const devolucionesTransferencia = notasSesion
        .filter(nota => ['transferencia', 'nequi', 'tarjeta'].includes(nota.metodo_pago?.toLowerCase()))
        .reduce((suma, nota) => suma + Number(nota.total_devolucion), 0);

    const printComponentRef = useRef(null);
    const handlePrint = useReactToPrint({
        contentRef: printComponentRef,
        documentTitle: "Cierre de Caja",
    });

    const historial = data?.historial || [];

    const usuariosUnicos = React.useMemo(() => {
        if (!historial) return [];
        const nombres = historial.map(h => h.usuario_nombre ? `${h.usuario_nombre} ${h.usuario_apellidos || ''}`.trim() : 'Administrador');
        return [...new Set(nombres)];
    }, [historial]);

    // Aplicar el filtro a la tabla
    const historialFiltrado = React.useMemo(() => {
        if (!historial) return [];
        if (usuarioFiltro === 'todos') return historial;
        return historial.filter(h => {
            const nombreCompleto = h.usuario_nombre ? `${h.usuario_nombre} ${h.usuario_apellidos || ''}`.trim() : 'Administrador';
            return nombreCompleto === usuarioFiltro;
        });
    }, [historial, usuarioFiltro]);

    if (isLoading) {
        return <div style={{ textAlign: 'center', padding: '100px 0' }}><Spin size="large" /><div style={{ marginTop: '16px', color: '#64748b' }}>Cuadrando cajas...</div></div>;
    }

    if (isError || !data) {
        return <div style={{ textAlign: 'center', marginTop: '50px' }}><Text type="danger">Error al cargar el flujo de caja.</Text></div>;
    }

    const { kpis } = data;

    const handleImprimirRecibo = (info) => {
        if (!info) return;

        setDatosImpresionCierre({
            id: info.id,
            nombreUsuario: detalle?.info?.usuario_nombre + ' ' + detalle?.info?.usuario_apellidos,
            creado_at: info.apertura,
            cierre: info.cierre,
            base_inicial: info.monto_inicial,
            ventas_totales: info.total_ventas_general,
            ventas_efectivo: info.total_ventas_efectivo,
            abonos_efectivo: info.total_abonos,
            ventas_transferencia: info.total_ventas_transferencia || 0,
            ventas_credito: info.ventasCredito,
            devoluciones_transferencia: devolucionesTransferencia,
            pagos_proveedores: info.total_compras || 0,
            gastos: info.total_gastos,
            devoluciones: info.total_devoluciones_efectivo || 0,
            monto_esperado: info.monto_esperado,
            monto_fisico: info.monto_fisico,
            diferencia: info.diferencia,
            observaciones: info.observaciones
        });

        setTimeout(() => {
            handlePrint();
        }, 500);
    }

    const reimprimirTicketBluetooth = async (datosBase, infoNegocio, tipoPapel) => {
        try {
            if (!navigator.bluetooth) {
                return message.error("Tu dispositivo no soporta conexión Bluetooth directa desde el navegador.");
            }

            const device = await navigator.bluetooth.requestDevice({
                acceptAllDevices: true,
                optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb']
            });

            const server = await device.gatt.connect();
            const service = await server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb');
            const characteristic = await service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb');

            const payload = generarTicketBluetooth(datosBase, infoNegocio, tipoPapel);
            const CHUNK_SIZE = 100;

            for (let i = 0; i < payload.length; i += CHUNK_SIZE) {
                const chunk = payload.slice(i, i + CHUNK_SIZE);
                await characteristic.writeValue(chunk);
                await new Promise(resolve => setTimeout(resolve, 150));
            }

            device.gatt.disconnect();
            message.success({ content: 'Reimpresión exitosa', key: 'imprimiendoBT' });

        } catch (error) {
            console.error("Error al reimprimir por Bluetooth:", error);
            message.error({ content: "Fallo la impresión. Verifica la impresora.", key: 'imprimiendoBT' });
        }
    };

    const handleReimprimirVenta = async (venta) => {
        if (!venta) return;

        const carritoMapeado = (productosVenta || []).map(item => ({
            codigo_barras: item.codigo_barras || 'SERVICIO',
            nombre: item.nombre,
            numItems: item.cantidad || 1,
            precioVenta: Number(item.precio_unitario) || 0,
            descuento: Number(item.descuento) || 0
        }));

        const datosCompletos = {
            carrito: carritoMapeado,
            totales: {
                subtotal: Number(venta.total) || 0,
                impuestos: 0,
                total: Number(venta.total) || 0,
                totalDescuento: 0
            },
            ventaId: venta.id,
            numeroFactura: venta.numero_factura,
            fechaVenta: venta.hora || new Date().toISOString(),
            clienteNombre: venta.cliente_nombre || 'CONSUMIDOR FINAL',
            clienteDocumento: venta.cliente_documento || '222222222222',
            clienteTelefono: venta.cliente_telefono || '',
            clienteDireccion: venta.cliente_direccion || '',
            cajero: detalle?.info?.usuario_nombre ? `${detalle.info.usuario_nombre} ${detalle.info.usuario_apellidos || ''}` : 'Cajero(a)',
            metodoPago: venta.metodo_pago || 'Efectivo',
            efectivoRecibido: Number(venta.total) || 0,
            cambioEntregado: 0,
            montoTransferencia: 0
        };

        const infoNegocio = {
            nombre: config.nombre,
            nombre_legal: config.nombre_legal,
            nit: config.identificacion_fiscal,
            direccion: config.direccion,
            telefono: config.telefono,
            logo_url: config.logo_url
        };

        const tipoImpresora = config?.impresora_tipo || config?.settings?.impresora_tipo || '80mm';
        const conexionImpresora = config?.settings?.conexion_impresora || 'sistema';

        if (tipoImpresora === 'carta_mitad') {
            const nuevaPestana = window.open('', '_blank');
            if (nuevaPestana) nuevaPestana.document.write('Generando documento oficial, por favor espere...');

            try {
                const blob = await pdf(<FacturaFormal {...datosCompletos} infoNegocio={infoNegocio} />).toBlob();
                const url = URL.createObjectURL(blob);

                if (nuevaPestana) {
                    nuevaPestana.location.href = url;
                } else {
                    window.open(url, '_blank');
                }
            } catch (error) {
                console.error("Error al construir el PDF: ", error);
                if (nuevaPestana) nuevaPestana.close();
            }
        } else if (conexionImpresora === 'bluetooth') {
            message.loading({ content: 'Conectando con impresora...', key: 'imprimiendoBT' });
            await reimprimirTicketBluetooth(datosCompletos, infoNegocio, tipoImpresora);
        } else {
            setDatosImpresionVenta(datosCompletos);
            setTimeout(() => { handlePrintVenta(); }, 300);
        }
    };

    const descargarPDFFormalDesdeFlujo = async (venta) => {
        const carritoMapeado = (productosVenta || []).map(item => ({
            codigo_barras: item.codigo_barras, nombre: item.nombre, numItems: item.cantidad || 1, precioVenta: Number(item.precio_unitario) || 0, descuento: Number(item.descuento) || 0
        }));

        const datosCompletos = {
            carrito: carritoMapeado,
            totales: { subtotal: Number(venta.total) || 0, impuestos: 0, total: Number(venta.total) || 0, totalDescuento: 0 },
            ventaId: venta.id, numeroFactura: venta.numero_factura, fechaVenta: venta.hora || new Date().toISOString(),
            clienteNombre: venta.cliente_nombre || 'CONSUMIDOR FINAL', clienteDocumento: venta.cliente_documento || '222222222222',
            clienteTelefono: venta.cliente_telefono || '', clienteDireccion: venta.cliente_direccion || '', clienteEmail: venta.cliente_email || '',
            cajero: detalle?.info?.usuario_nombre ? `${detalle.info.usuario_nombre} ${detalle.info.usuario_apellidos || ''}` : 'Cajero(a)',
            metodoPago: venta.metodo_pago || 'Efectivo',
            efectivoRecibido: Number(venta.total) || 0,
            cambioEntregado: 0, montoTransferencia: 0
        };

        const infoNegocio = {
            nombre: config.nombre, nombre_legal: config.nombre_legal, nit: config.identificacion_fiscal,
            direccion: config.direccion, telefono: config.telefono, logo_url: config.logo_url
        };

        const nuevaPestana = window.open('', '_blank');
        if (nuevaPestana) nuevaPestana.document.write('Generando PDF, por favor espere...');

        try {
            const blob = await pdf(<FacturaFormal {...datosCompletos} infoNegocio={infoNegocio} />).toBlob();
            if (nuevaPestana) { nuevaPestana.location.href = URL.createObjectURL(blob); }
        } catch (error) {
            console.error(error);
            if (nuevaPestana) nuevaPestana.close();
        }
    };

    return (
        <div style={{ animation: 'fadeIn 0.5s' }}>
            {/* KPIs Financieros */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} styles={{ body: { padding: '20px' } }} style={{ background: '#f0fdf4', borderRadius: '12px', border: '1px solid #dcfce3' }}>
                        <Statistic
                            title={
                                <Space>
                                    <Text type="secondary" style={{ fontSize: '13px' }}>Dinero Entrante (Ingresos)</Text>
                                    <AntTooltip title="Todo el dinero real que entró a tus cajas y bancos en este periodo. Nota: Puede no coincidir exactamente con 'Ventas Brutas' si tienes turnos de caja que cruzan la medianoche o ventas fiadas que aún no te han pagado." color="#0f172a">
                                        <InfoCircleOutlined style={{ color: '#10b981', fontSize: '12px' }} />
                                    </AntTooltip>
                                </Space>
                            }
                            value={kpis.ingresos}
                            prefix={<RiseOutlined style={{ color: '#10b981' }} />}
                            formatter={val => `$${Number(val).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`}
                            valueStyle={{ fontWeight: '700', color: '#10b981', fontSize: '20px' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} styles={{ body: { padding: '20px' } }} style={{ background: '#fef2f2', borderRadius: '12px', border: '1px solid #fee2e2' }}>
                        <Statistic
                            title={
                                <Space>
                                    <Text type="secondary" style={{ fontSize: '13px' }}>Dinero Saliente (Egresos)</Text>
                                    <AntTooltip title="La suma de todos los billetes y transferencias que salieron de tu negocio para pagar mercancía de contado y gastos diarios (como recibos, arriendos o nómina)." color="#0f172a">
                                        <InfoCircleOutlined style={{ color: '#ef4444', fontSize: '12px' }} />
                                    </AntTooltip>
                                </Space>
                            }
                            value={kpis.egresos}
                            prefix={<FallOutlined style={{ color: '#ef4444' }} />}
                            formatter={val => `$${Number(val).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`}
                            valueStyle={{ fontWeight: '700', color: '#ef4444', fontSize: '20px' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} styles={{ body: { padding: '20px' } }} style={{ background: '#e0f2fe', borderRadius: '12px', border: '1px solid #bae6fd' }}>
                        <Statistic
                            title={
                                <Space>
                                    <Text type="secondary" style={{ fontSize: '13px' }}>Dinero Libre (Caja Neta)</Text>
                                    <AntTooltip title="Es la plata física y en bancos que te quedó disponible. Básicamente: Todo el dinero que entró, menos todo el dinero que salió." color="#0f172a">
                                        <InfoCircleOutlined style={{ color: '#0ea5e9', fontSize: '12px' }} />
                                    </AntTooltip>
                                </Space>
                            }
                            value={kpis.flujoNeto}
                            prefix={<DollarOutlined style={{ color: '#0ea5e9' }} />}
                            formatter={val => `$${Number(val).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`}
                            valueStyle={{ fontWeight: '700', color: '#0ea5e9', fontSize: '20px' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} styles={{ body: { padding: '20px' } }} style={{ background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <Statistic
                            title={
                                <Space>
                                    <Text type="secondary" style={{ fontSize: '13px' }}>Salud del Efectivo</Text>
                                    <AntTooltip title="Mide qué tan bien retienes el dinero. Por ejemplo: Si dice 20%, significa que de cada $100 pesos que entraron a la caja, lograste conservar $20 libres después de pagar todo." color="#0f172a">
                                        <InfoCircleOutlined style={{ color: '#64748b', fontSize: '12px' }} />
                                    </AntTooltip>
                                </Space>
                            }
                            value={kpis.margen}
                            suffix="%"
                            formatter={val => Number(val).toFixed(1)}
                            valueStyle={{ fontWeight: '700', color: '#0f172a', fontSize: '20px' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* TABLA DE AUDITORÍA 100% ANCHO */}
            <Row gutter={[24, 24]}>
                <Col xs={24}>
                    <Card
                        title={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                                <Space>
                                    <Title level={5} style={{ margin: 0 }}>Auditoría de Cierres de Caja</Title>
                                    <AntTooltip title="Historial detallado de cada vez que se abrió y cerró la caja." color="#0f172a">
                                        <InfoCircleOutlined style={{ color: '#94a3b8', fontSize: '14px' }} />
                                    </AntTooltip>
                                </Space>

                                <Space>
                                    <Text type="secondary" style={{ fontSize: '13px' }}>Filtrar por Cajero:</Text>
                                    <Select
                                        value={usuarioFiltro}
                                        onChange={setUsuarioFiltro}
                                        style={{ width: 200 }}
                                        options={[
                                            { value: 'todos', label: 'Todos los usuarios' },
                                            ...usuariosUnicos.map(u => ({ value: u, label: u }))
                                        ]}
                                    />
                                </Space>
                            </div>
                        }
                        bordered={false}
                        style={{ borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)', height: '100%' }}
                    >
                        <Table
                            dataSource={historialFiltrado}
                            rowKey="id"
                            size="middle"
                            pagination={{ pageSize: 8 }}
                            scroll={{ x: 'max-content' }}
                            columns={[
                                {
                                    title: 'Turno',
                                    dataIndex: 'fecha',
                                    render: (text, record) => (
                                        <Space direction="vertical" size={0}>
                                            <Text strong style={{ color: '#334155', fontSize: '13px' }}>Abre: {text}</Text>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                Cierra: {record.cierre || record.fecha_cierre || (record.estado === 'CERRADA' ? 'Sin registrar' : 'Turno en curso')}
                                            </Text>
                                        </Space>
                                    )
                                },
                                {
                                    title: 'Responsable',
                                    align: 'left',
                                    render: (_, record) => (
                                        <Space>
                                            <UserOutlined style={{ color: '#94a3b8' }} />
                                            <Text strong style={{ color: '#475569' }}>
                                                {record.usuario_nombre ? `${record.usuario_nombre} ${record.usuario_apellidos || ''}` : 'Administrador'}
                                            </Text>
                                        </Space>
                                    )
                                },
                                {
                                    title: 'Base',
                                    dataIndex: 'base',
                                    align: 'right',
                                    render: (_, record) => <Text type="secondary">$ {Number(record.monto_inicial || record.base || 0).toLocaleString('es-CO')}</Text>
                                },
                                {
                                    title: 'Total Ventas',
                                    align: 'right',
                                    // Usamos total_ventas_general (como el modal) y dejamos fallback a las otras opciones
                                    render: (_, record) => <Text>$ {Number(record.total_ventas_general || record.total_ventas || record.ventas || 0).toLocaleString('es-CO')}</Text>
                                },
                                {
                                    title: 'Efectivo Esperado',
                                    align: 'right',
                                    render: (_, record) => <Text>$ {Number(record.monto_esperado || record.esperado || 0).toLocaleString('es-CO')}</Text>
                                },
                                {
                                    title: 'Efectivo Recibido',
                                    align: 'right',
                                    render: (_, record) => <Text strong style={{ color: '#22BABB' }}>$ {Number(record.monto_fisico || record.real || 0).toLocaleString('es-CO')}</Text>
                                },
                                {
                                    title: 'Diferencia',
                                    align: 'right',
                                    render: (_, record) => {
                                        const val = Number(record.diferencia || 0);
                                        return (
                                            <Text strong style={{ color: val < 0 ? '#ef4444' : val > 0 ? '#10b981' : '#94a3b8' }}>
                                                {val > 0 ? '+' : ''}{val === 0 ? '$0' : `$${val.toLocaleString('es-CO')}`}
                                            </Text>
                                        )
                                    }
                                },
                                {
                                    title: 'Estado',
                                    dataIndex: 'estado',
                                    align: 'center',
                                    render: (estado) => (
                                        <Tag color={estado === 'CERRADA' ? 'success' : 'processing'} style={{ borderRadius: '4px', fontWeight: 'bold' }}>
                                            {estado}
                                        </Tag>
                                    )
                                },
                                {
                                    title: 'Acción',
                                    align: 'center',
                                    render: (_, record) => (
                                        <Button
                                            type="primary"
                                            size="small"
                                            style={{ backgroundColor: '#22BABB', borderRadius: '6px' }}
                                            icon={<EyeOutlined />}
                                            onClick={() => setSesionIdSeleccionada(record.id)}
                                        >
                                            Ver Detalles
                                        </Button>
                                    )
                                }
                            ]}
                        />
                    </Card>
                </Col>
            </Row>

            {/* MODAL DE AUDITORÍA DETALLADA */}
            <Modal
                title={null}
                open={!!sesionIdSeleccionada}
                onCancel={() => setSesionIdSeleccionada(null)}
                footer={null}
                width={850}
                centered
                styles={{ body: { padding: '24px' } }}
            >
                {loadingDetalle ? <div style={{ textAlign: 'center', padding: '40px' }}><Spin /></div> : (
                    <div style={{ animation: 'fadeIn 0.3s' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                            <div>
                                <Title level={3} style={{ margin: 0 }}>Reporte de Cierre de Caja</Title>
                                <Text type="secondary">ID de sesión: {detalle?.info?.id}</Text>
                            </div>
                            <Space>
                                <Button
                                    type="primary"
                                    icon={<PrinterOutlined />}
                                    onClick={() => handleImprimirRecibo(detalle?.info)}
                                    style={{ backgroundColor: '#22BABB' }}
                                >
                                    Imprimir Cierre
                                </Button>
                                <Tag color={detalle?.info?.estado === 'CERRADA' ? 'success' : 'processing'} style={{ fontSize: '14px', padding: '4px 12px', margin: 0 }}>
                                    {detalle?.info?.estado}
                                </Tag>
                            </Space>
                        </div>

                        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                            <Col xs={24} md={12}>
                                <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', height: '100%' }}>
                                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <ClockCircleOutlined style={{ color: '#22BABB' }} />
                                            <Text strong>Tiempos de Turno</Text>
                                        </div>
                                        <div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Text type="secondary">Apertura:</Text>
                                                <Text strong>{detalle?.info?.apertura}</Text>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                                                <Text type="secondary">Cierre:</Text>
                                                <Text strong>{detalle?.info?.cierre || 'Turno activo'}</Text>
                                            </div>
                                        </div>
                                    </Space>
                                </div>
                            </Col>
                            <Col xs={24} md={12}>
                                <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', height: '100%' }}>
                                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <UserOutlined style={{ color: '#22BABB' }} />
                                            <Text strong>Responsable</Text>
                                        </div>
                                        <Text strong style={{ fontSize: '16px' }}>{detalle?.info?.usuario_nombre || 'Administrador'} {detalle?.info?.usuario_apellidos || ''}</Text>
                                        <Text type="secondary" size="small">El cajero confirmó el cierre con firma digital.</Text>
                                    </Space>
                                </div>
                            </Col>
                        </Row>

                        {/* --- DESGLOSE DETALLADO DE CAJA --- */}
                        <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
                            <Title level={5} style={{ marginBottom: '16px', color: '#0f172a' }}>Desglose de Movimientos</Title>
                            <Row gutter={[16, 24]}>
                                <Col xs={12} sm={8} lg={6}>
                                    <Statistic title="Base Inicial" value={detalle?.info?.monto_inicial || 0} prefix="$" formatter={v => Number(v).toLocaleString('es-CO')} />
                                </Col>
                                <Col xs={12} sm={8} lg={6}>
                                    <Statistic title="Total Ventas (Todas)" value={detalle?.info?.total_ventas_general || 0} prefix="$" formatter={v => Number(v).toLocaleString('es-CO')} valueStyle={{ color: '#0ea5e9' }} />
                                </Col>
                                <Col xs={12} sm={8} lg={6}>
                                    <Statistic title="Abonos Clientes" value={detalle?.info?.total_abonos || 0} prefix="$" formatter={v => Number(v).toLocaleString('es-CO')} valueStyle={{ color: '#10b981' }} />
                                </Col>
                                <Col xs={12} sm={8} lg={6}>
                                    <Statistic title="Gastos Diarios" value={detalle?.info?.total_gastos || 0} prefix="$" formatter={v => Number(v).toLocaleString('es-CO')} valueStyle={{ color: '#ef4444' }} />
                                </Col>
                                <Col xs={12} sm={8} lg={6}>
                                    <Statistic title="Pagos / Compras" value={detalle?.info?.total_compras || 0} prefix="$" formatter={v => Number(v).toLocaleString('es-CO')} valueStyle={{ color: '#fa8c16' }} />
                                </Col>
                                <Col xs={12} sm={8} lg={6}>
                                    <Statistic title="Devoluciones Efectivo" value={detalle?.info?.total_devoluciones_efectivo || 0} prefix="-$" formatter={v => Number(v).toLocaleString('es-CO')} valueStyle={{ color: '#ef4444' }} />
                                </Col>
                                <Col xs={12} sm={8} lg={6}>
                                    <Statistic title="Efectivo Esperado" value={detalle?.info?.monto_esperado || 0} prefix="$" formatter={v => Number(v).toLocaleString('es-CO')} valueStyle={{ fontWeight: 'bold' }} />
                                </Col>
                                <Col xs={12} sm={8} lg={6}>
                                    <Statistic title="Efectivo Recibido" value={detalle?.info?.monto_fisico || 0} prefix="$" formatter={v => Number(v).toLocaleString('es-CO')} valueStyle={{ fontWeight: 'bold', color: '#22BABB' }} />
                                </Col>
                            </Row>
                            <Divider style={{ margin: '16px 0' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text strong style={{ fontSize: '15px', color: '#64748b' }}>Diferencia (Sobrante / Faltante):</Text>
                                <Text strong style={{ fontSize: '22px', color: (detalle?.info?.diferencia || 0) < 0 ? '#ef4444' : '#10b981' }}>
                                    {(detalle?.info?.diferencia || 0) > 0 ? '+' : ''}
                                    ${Number(detalle?.info?.diferencia || 0).toLocaleString('es-CO')}
                                </Text>
                            </div>
                        </div>

                        <Divider />

                        <Row gutter={[24, 24]}>
                            <Col xs={24} md={10}>
                                <Title level={5} style={{ marginBottom: '16px' }}>Ventas por Medio de Pago</Title>
                                <List
                                    bordered
                                    style={{ borderRadius: '12px', backgroundColor: '#fff' }}
                                    dataSource={detalle?.metodos}
                                    renderItem={item => {
                                        const isTransf = ['transferencia', 'nequi', 'tarjeta'].includes(item.metodo_pago?.toLowerCase());
                                        const deduccion = isTransf ? devolucionesTransferencia : 0;
                                        const neto = Number(item.total) - deduccion;

                                        return (
                                            <List.Item style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                    <Text strong style={{ textTransform: 'capitalize', fontSize: '14px' }}>
                                                        {item.metodo_pago}
                                                    </Text>
                                                    <Text strong style={{ color: '#22BABB', fontSize: '15px' }}>
                                                        ${neto.toLocaleString('es-CO')}
                                                    </Text>
                                                </div>
                                                {deduccion > 0 && (
                                                    <Text style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px' }}>
                                                        (Valor bruto: ${Number(item.total).toLocaleString('es-CO')} | Valor devuelto: -${deduccion.toLocaleString('es-CO')})
                                                    </Text>
                                                )}
                                            </List.Item>
                                        );
                                    }}
                                />
                            </Col>
                            <Col xs={24} md={14}>
                                <Title level={5} style={{ marginBottom: '16px' }}>Historial de Facturación</Title>
                                <Table
                                    dataSource={detalle?.ventas}
                                    size="small"
                                    pagination={{ pageSize: 5 }}
                                    columns={[
                                        {
                                            title: 'Fecha y Hora',
                                            dataIndex: 'hora',
                                            render: (v) => <Text>{v || '-'}</Text>
                                        },
                                        { title: 'Factura', dataIndex: 'numero_factura', render: v => <Text type="secondary">{v}</Text> },
                                        { title: 'Total', dataIndex: 'total', align: 'right', render: v => <Text strong>${Number(v).toLocaleString('es-CO')}</Text> },
                                        {
                                            title: 'Acciones', align: 'center',
                                            render: (_, record) => (
                                                <Space size="small">
                                                    <AntTooltip title="Ver Detalles"><Button size="small" type="text" icon={<EyeOutlined />} onClick={() => setVentaSeleccionada(record)} /></AntTooltip>
                                                    <AntTooltip title="Reimprimir"><Button size="small" type="text" icon={<PrinterOutlined />} style={{ color: '#22BABB', background: '#e0f2fe' }} onClick={() => handleReimprimirVenta(record)} /></AntTooltip>
                                                    <AntTooltip title="Descargar PDF"><Button size="small" type="text" icon={<FilePdfOutlined />} style={{ color: '#d32f2f', background: '#fce4e4' }} onClick={() => descargarPDFFormalDesdeFlujo(record)} /></AntTooltip>
                                                </Space>
                                            )
                                        },
                                    ]}
                                />
                            </Col>
                        </Row>

                        {detalle?.info?.observaciones && (
                            <div style={{ marginTop: '24px', padding: '16px', background: '#fffbeb', borderRadius: '12px', border: '1px solid #fef3c7' }}>
                                <Text strong><InfoCircleOutlined /> Observaciones del Cierre:</Text>
                                <div style={{ marginTop: '8px' }}><Text italic>"{detalle?.info?.observaciones}"</Text></div>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            <Modal
                title={<Space><FileTextOutlined style={{ color: '#22BABB' }} /><Text strong>DETALLE DE VENTA</Text></Space>}
                open={!!ventaSeleccionada}
                onCancel={() => setVentaSeleccionada(null)}
                width={400}
                centered
                footer={[
                    <Button
                        key="print"
                        type="primary"
                        block
                        icon={<PrinterOutlined />}
                        onClick={() => handleReimprimirVenta(ventaSeleccionada)}
                        style={{ background: '#22BABB', height: '45px', fontWeight: 'bold' }}
                    >
                        REIMPRIMIR DOCUMENTO
                    </Button>
                ]}
                styles={{ body: { padding: 0 } }}
            >
                {ventaSeleccionada && (
                    <div style={{ padding: '25px', backgroundColor: '#fff', fontFamily: 'monospace', height: 'calc(100vh - 125px)', overflowY: 'auto' }}>
                        <style>{`
                            .ticket-scroll::-webkit-scrollbar { width: 4px; }
                            .ticket-scroll::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 4px; }
                        `}</style>
                        <div style={{ textAlign: 'center', marginBottom: 15 }}>
                            <Title level={5} style={{ margin: 0 }}>{ventaSeleccionada.numero_factura ? `RECIBO #${ventaSeleccionada.numero_factura}` : 'TICKET DE VENTA'}</Title>
                            <Text style={{ fontSize: '11px' }}>{ventaSeleccionada.hora || ''}</Text>
                        </div>
                        <Divider dashed style={{ margin: '10px 0' }} />
                        <div style={{ fontSize: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text>CLIENTE:</Text><Text>{ventaSeleccionada.cliente_nombre?.toUpperCase() || 'CONSUMIDOR FINAL'}</Text></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text>CC/NIT:</Text><Text>{ventaSeleccionada.cliente_documento || '222222222222'}</Text></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text>MEDIO DE PAGO:</Text><Text>{ventaSeleccionada.metodo_pago?.toUpperCase()}</Text></div>
                        </div>
                        <Divider dashed style={{ margin: '10px 0' }} />
                        <div className="ticket-scroll" style={{ marginBottom: 15, maxHeight: '220px', overflowY: 'auto', paddingRight: '8px' }}>
                            {loadingVenta ? <Spin style={{ display: 'block', margin: '20px auto' }} /> : (
                                productosVenta?.map((item, i) => (
                                    <div key={i} style={{ marginBottom: 8, fontSize: '12px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ flex: 1 }}>{item.nombre?.toUpperCase()}</span>
                                            <Text strong>${Number(item.subtotal).toLocaleString('es-CO')}</Text>
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#666' }}>{item.cantidad} UND x ${Number(item.precio_unitario).toLocaleString('es-CO')}</div>
                                    </div>
                                ))
                            )}
                        </div>
                        <Divider dashed style={{ margin: '10px 0' }} />
                        <div style={{ fontSize: '13px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text>TOTAL VENTA:</Text><Text strong style={{ fontSize: '18px' }}>${Number(ventaSeleccionada.total).toLocaleString('es-CO')}</Text></div>
                        </div>
                        <Divider dashed style={{ margin: '10px 0' }} />
                        <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '4px', fontSize: '11px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text strong>MÉTODO:</Text><Text>{ventaSeleccionada.metodo_pago?.toUpperCase()}</Text></div>
                            {ventaSeleccionada.metodo_pago === 'Efectivo' && (
                                <>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text>RECIBIDO:</Text><Text>${Number(ventaSeleccionada.efectivo_recibido || 0).toLocaleString('es-CO')}</Text></div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text>CAMBIO:</Text><Text strong>${Number(ventaSeleccionada.cambio_entregado || 0).toLocaleString('es-CO')}</Text></div>
                                </>
                            )}
                            {ventaSeleccionada.metodo_pago === 'Mixto' && (
                                <>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text>EFECTIVO:</Text><Text>${Number(ventaSeleccionada.monto_efectivo || 0).toLocaleString('es-CO')}</Text></div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}><Text>TRANSF.:</Text><Text>${Number(ventaSeleccionada.monto_transferencia || 0).toLocaleString('es-CO')}</Text></div>
                                </>
                            )}
                            {ventaSeleccionada.metodo_pago === 'Credito' && (
                                <div style={{ marginTop: '8px', borderTop: '1px dashed #cbd5e1', paddingTop: '8px' }}>
                                    {Number(ventaSeleccionada.efectivo_recibido) > 0 && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                            <Text>ABONO INICIAL:</Text>
                                            <Text>${Number(ventaSeleccionada.efectivo_recibido).toLocaleString('es-CO')}</Text>
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#cf1322', fontSize: '16px', background: '#fef2f2', padding: '4px', borderRadius: '4px' }}>
                                        <Text strong style={{ color: '#cf1322' }}>SALDO PENDIENTE:</Text>
                                        <Text strong style={{ color: '#cf1322' }}>
                                            ${Number((ventaSeleccionada.total || 0) - (ventaSeleccionada.efectivo_recibido || 0)).toLocaleString('es-CO')}
                                        </Text>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div style={{ textAlign: 'center', marginTop: 15, fontSize: '10px', color: '#999' }}>*** YUGONUB POS - GRACIAS ***</div>
                    </div>
                )}
            </Modal>

            <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
                <div ref={printComponentRef} style={{ display: 'flex', justifyContent: 'center', minHeight: '100vh', padding: '20px', backgroundColor: '#ffffff' }}>
                    {datosImpresionCierre && (
                        <TicketCierreCaja datosCierre={datosImpresionCierre} infoNegocio={{ nombre: config.nombre, nit: config.identificacion_fiscal, anchoTicket: config.settings?.impresora_tipo }} />
                    )}
                </div>
            </div>

            <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
                <div ref={ventaPrintComponentRef} style={{ display: 'flex', justifyContent: 'center', minHeight: '100vh', padding: '5px 2px 20px 2px', backgroundColor: '#F0F2F4' }}>
                    {datosImpresionVenta && (
                        <TicketVenta
                            carrito={datosImpresionVenta.carrito}
                            totales={datosImpresionVenta.totales}
                            ventaId={datosImpresionVenta.ventaId}
                            numeroFactura={datosImpresionVenta.numeroFactura}
                            fechaVenta={datosImpresionVenta.fechaVenta}
                            clienteNombre={datosImpresionVenta.clienteNombre}
                            clienteDocumento={datosImpresionVenta.clienteDocumento}
                            clienteTelefono={datosImpresionVenta.clienteTelefono}
                            clienteDireccion={datosImpresionVenta.clienteDireccion}
                            cajero={datosImpresionVenta.cajero}
                            metodoPago={datosImpresionVenta.metodoPago}
                            cambioEntregado={datosImpresionVenta.cambioEntregado}
                            efectivoRecibido={datosImpresionVenta.efectivoRecibido}
                            montoTransferencia={datosImpresionVenta.montoTransferencia}
                            infoNegocio={{
                                nombre: config.nombre,
                                nombre_legal: config.nombre_legal,
                                nit: config.identificacion_fiscal,
                                direccion: config.direccion,
                                telefono: config.telefono,
                                anchoTicket: config.impresora_tipo || config.settings?.impresora_tipo || '57mm',
                                logo: config.logo
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    )
}

export default SeccionFlujoCaja;